import { Alert, BaseSearchInput, Button, List } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import { useWithdrawDataAtom } from '@capsicum/features/wallet/shared';
import { type StaticImageData } from 'next/image';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { TokenSelectionListCard } from '../TokenSelectionListCard';

export type FanTokenSelectorItem = {
	amount?: number;
	enabled?: boolean;
	formattedAmount?: string;
	id?: string;
	isCC2Enabled?: boolean;
	minAmount?: number;
	partnerName?: string;
	shortCode?: string;
	url?: StaticImageData | string;
};

export type FanTokenSelectorModal = {
	buttonText?: string;
	data: Array<FanTokenSelectorItem>;
	onClick: (chosenItem: FanTokenSelectorItem) => void;
	texts?: {
		button?: string;
		searchInputPlaceholder?: string;
		title?: string;
		warning?: string;
	};
};

export type FanTokenSelectorData = {
	items: Array<FanTokenSelectorItem>;
};

export function FanTokenSelectorModal({ buttonText, data, onClick, texts }: FanTokenSelectorModal) {
	const { button, searchInputPlaceholder, title, warning } = texts || {};

	const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
	const [searchValue, setSearchValue] = useState('');
	const { setFanToken } = useWithdrawDataAtom();
	const LIST_ITEM_DEFAULT_HEIGHT = 100;

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const text = event.target.value || '';

		setSearchValue(text);
	};

	const filteredFanTokens = useMemo(
		() =>
			data.filter((item: FanTokenSelectorItem) =>
				item.partnerName?.toLowerCase().includes(searchValue.toLowerCase())
			),
		[searchValue, data]
	);

	const completeSelection = () => {
		const selectedItem = filteredFanTokens.find(item => item.id === selectedTokenId);

		setFanToken(selectedItem);

		!!selectedItem && onClick(selectedItem);
	};

	const renderItem = ({ data, index }: { data: FanTokenSelectorData; index: number }) => {
		const { items } = data;
		const item = items[index];

		return (
			<TokenSelectionListCard
				item={item!}
				key={item!.id}
				selected={selectedTokenId === item!.id}
				selectToken={setSelectedTokenId}
			/>
		);
	};

	const listHeight = useMemo(() => LIST_ITEM_DEFAULT_HEIGHT * filteredFanTokens?.length, [filteredFanTokens?.length]);

	return (
		<S.Container>
			<S.Title>{title}</S.Title>
			<BaseSearchInput
				name="fan-token-selector-modal-search-input"
				placeholder={searchInputPlaceholder}
				testID="fan-token-selector-modal-search-input"
				onChange={handleSearch}
			/>
			{!!warning && searchValue === '' && (
				<S.AlertWrapper>
					<Alert
						closable={false}
						icon={AlertIcon}
						subTitle={warning}
						testID="fan-token-selector-modal-warning"
						title=""
						type="important"
					/>
				</S.AlertWrapper>
			)}
			<S.FanTokenContainer>
				<List
					height={listHeight}
					isItemLoaded={(index: number) => index < 20}
					itemCount={filteredFanTokens?.length || 0}
					itemData={{ items: filteredFanTokens }}
					itemSize={LIST_ITEM_DEFAULT_HEIGHT}
					width="100%"
				>
					{renderItem}
				</List>
			</S.FanTokenContainer>
			<S.Wrapper>
				<Button
					disabled={!selectedTokenId}
					testID="fan-token-selector-button"
					text={buttonText || button}
					onClick={completeSelection}
				/>
			</S.Wrapper>
		</S.Container>
	);
}

namespace S {
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		position: relative;
		text-align: left;
		margin: 0;
		flex: 1;
		padding: 0 ${({ theme }) => theme.spacings.medium} 0 0;
		height: 700px;
	`;
	export const FanTokenContainer = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
	`;
	export const AlertWrapper = styled.div`
		display: flex;
		width: 100%;
		margin: ${({ theme }) => theme.spacings.small} 0;
	`;
	export const Title = styled.p`
		display: flex;
		width: 100%;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin: ${({ theme }) => theme.spacings.xsmall} 0;
		text-align: left;
	`;
	export const Wrapper = styled.div`
		display: flex;
		position: sticky;
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding-top: ${({ theme }) => theme.spacings.large};
		bottom: 0;
		z-index: 9;
		align-self: center;
		width: 100%;
		align-items: center;
		justify-content: center;
	`;
}
